import { useState, useCallback, useEffect } from "react";
import { apiService } from "../../../../services/api";
import { Gesamtstatus, GesamtstatusDomain, Windpark } from "../../../../types";

interface UseProjectStatusDataProps {
  project: Windpark;
  onProjectUpdated?: () => void;
}

interface StatusOptions {
  flaechenakquise: string[];
  planungsrecht: string[];
  bimschg: string[];
  eegAusschreibung: string[];
  finanzierung: string[];
  bau: string[];
}

interface UseProjectStatusDataReturn {
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  options: StatusOptions;
  gesamtstatus: string;
  gesamtstatusDomain: GesamtstatusDomain;
  gesamtstatusAbgeschlossen: boolean;
  flaechenakquiseStatus: string;
  finanzierungStatus: string;
  updateFlaechenakquiseStatus: (value: string) => Promise<void>;
  updatePlanungsrechtStatus: (value: string) => Promise<void>;
  updateBimschgStatus: (value: string) => Promise<void>;
  updateEegAusschreibungStatus: (value: string) => Promise<void>;
  updateFinanzierungStatus: (value: string) => Promise<void>;
  updateBauStatus: (value: string) => Promise<void>;
}

const EMPTY_OPTIONS: StatusOptions = {
  flaechenakquise: [],
  planungsrecht: [],
  bimschg: [],
  eegAusschreibung: [],
  finanzierung: [],
  bau: [],
};

/**
 * Hook für die 6 Domänen-Status eines Projekts (Section "Projektstatus" in der Übersicht).
 * Lädt Area/Financing separat, da flaechenakquise_status/finanzierung_status nicht
 * Teil des Projekt-Objekts sind (liegen auf AreaDB bzw. FinancingDB).
 */
export const useProjectStatusData = ({
  project,
  onProjectUpdated,
}: UseProjectStatusDataProps): UseProjectStatusDataReturn => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<StatusOptions>(EMPTY_OPTIONS);
  const [flaechenakquiseStatus, setFlaechenakquiseStatus] = useState("");
  const [finanzierungStatus, setFinanzierungStatus] = useState("");
  const [gesamtstatus, setGesamtstatus] = useState<Gesamtstatus | null>(null);

  const loadStatusData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [projectOptions, areaOptions, financingOptions, area, financing] =
        await Promise.all([
          apiService.getStatusOptions(),
          apiService.getFlaechenakquiseStatusOptions(),
          apiService.getFinanzierungStatusOptions(),
          apiService.getProjectArea(project.id).catch(() => null),
          apiService.getFinancing(project.id).catch(() => null),
        ]);

      setOptions({
        flaechenakquise: areaOptions.options,
        planungsrecht: projectOptions.planungsrecht_status_options,
        bimschg: projectOptions.bimschg_status_options,
        eegAusschreibung: projectOptions.eeg_ausschreibung_status_options,
        finanzierung: financingOptions.options,
        bau: projectOptions.bau_status_options,
      });
      setFlaechenakquiseStatus(area?.flaechenakquise_status ?? "");
      setFinanzierungStatus(financing?.finanzierung_status ?? "");
    } finally {
      setIsLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    loadStatusData();
  }, [loadStatusData]);

  const updatePlanungsrechtStatus = useCallback(
    async (value: string) => {
      if (value === project.planungsrecht_status) return;
      try {
        setIsSaving(true);
        setError(null);
        await apiService.updateProjectPartial(project.id, {
          planungsrecht_status: value,
        });
        onProjectUpdated?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Fehler beim Speichern");
      } finally {
        setIsSaving(false);
      }
    },
    [project.id, project.planungsrecht_status, onProjectUpdated],
  );

  const updateBimschgStatus = useCallback(
    async (value: string) => {
      if (value === project.bimschg_status) return;
      try {
        setIsSaving(true);
        setError(null);
        await apiService.updateProjectPartial(project.id, {
          bimschg_status: value,
        });
        onProjectUpdated?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Fehler beim Speichern");
      } finally {
        setIsSaving(false);
      }
    },
    [project.id, project.bimschg_status, onProjectUpdated],
  );

  const updateEegAusschreibungStatus = useCallback(
    async (value: string) => {
      if (value === project.eeg_ausschreibung_status) return;
      try {
        setIsSaving(true);
        setError(null);
        await apiService.updateProjectPartial(project.id, {
          eeg_ausschreibung_status: value,
        });
        onProjectUpdated?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Fehler beim Speichern");
      } finally {
        setIsSaving(false);
      }
    },
    [project.id, project.eeg_ausschreibung_status, onProjectUpdated],
  );

  const updateBauStatus = useCallback(
    async (value: string) => {
      if (value === project.bau_status) return;
      try {
        setIsSaving(true);
        setError(null);
        await apiService.updateProjectPartial(project.id, {
          bau_status: value,
        });
        onProjectUpdated?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Fehler beim Speichern");
      } finally {
        setIsSaving(false);
      }
    },
    [project.id, project.bau_status, onProjectUpdated],
  );

  const updateFlaechenakquiseStatus = useCallback(
    async (value: string) => {
      if (value === flaechenakquiseStatus) return;
      try {
        setIsSaving(true);
        setError(null);
        await apiService.updateProjectArea(project.id, {
          flaechenakquise_status: value,
        });
        setFlaechenakquiseStatus(value);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Fehler beim Speichern");
      } finally {
        setIsSaving(false);
      }
    },
    [project.id, flaechenakquiseStatus],
  );

  const updateFinanzierungStatus = useCallback(
    async (value: string) => {
      if (value === finanzierungStatus) return;
      try {
        setIsSaving(true);
        setError(null);
        await apiService.updateFinancing(project.id, {
          finanzierung_status: value,
        });
        setFinanzierungStatus(value);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Fehler beim Speichern");
      } finally {
        setIsSaving(false);
      }
    },
    [project.id, finanzierungStatus],
  );

  /**
   * Der Gesamtstatus kommt vom Server (Regel `projects/regeln/gesamtstatus.py`) und wird nach
   * jeder Änderung eines der sechs Status neu abgefragt: Projektstatus ändern das Projekt-Objekt,
   * Fläche und Finanzierung die beiden lokalen Werte.
   */
  useEffect(() => {
    let aktuell = true;
    apiService
      .getGesamtstatus(project.id)
      .then((ergebnis) => {
        if (aktuell) setGesamtstatus(ergebnis);
      })
      .catch(() => {
        if (aktuell) setGesamtstatus(null);
      });
    return () => {
      aktuell = false;
    };
  }, [project, flaechenakquiseStatus, finanzierungStatus]);

  return {
    isLoading,
    isSaving,
    error,
    options,
    gesamtstatus: gesamtstatus?.gesamtstatus ?? "",
    gesamtstatusDomain: gesamtstatus?.gesamtstatus_domain ?? "neu",
    gesamtstatusAbgeschlossen:
      gesamtstatus?.gesamtstatus_abgeschlossen ?? false,
    flaechenakquiseStatus,
    finanzierungStatus,
    updateFlaechenakquiseStatus,
    updatePlanungsrechtStatus,
    updateBimschgStatus,
    updateEegAusschreibungStatus,
    updateFinanzierungStatus,
    updateBauStatus,
  };
};
